import { Mail, MessageCircle, MessageSquare, Mic } from 'lucide-react-native';
import { useCallback } from 'react';
import { StyleSheet } from 'react-native';
import { Box, HStack, Text } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { isWhatsAppEnabled } from '../../../lib/featureFlags';
import { colors } from '../../../theme/colors';
import { CampaignChannel } from '../types';

const CHANNEL_CONFIG: Record<CampaignChannel, { icon: typeof Mail; labelKey: string }> = {
  [CampaignChannel.EMAIL]: { icon: Mail, labelKey: 'campaigns.channels.email' },
  [CampaignChannel.SMS]: { icon: MessageSquare, labelKey: 'campaigns.channels.sms' },
  [CampaignChannel.WHATSAPP]: { icon: MessageCircle, labelKey: 'campaigns.channels.whatsapp' },
  [CampaignChannel.VOICE]: { icon: Mic, labelKey: 'campaigns.channels.voice' },
};

interface ChannelSelectorProps {
  selected: CampaignChannel[];
  onChange: (channels: CampaignChannel[]) => void;
}

export function ChannelSelector({ selected, onChange }: Readonly<ChannelSelectorProps>) {
  const { t } = useTranslation();

  const toggleChannel = useCallback(
    (channel: CampaignChannel) => {
      if (selected.includes(channel)) {
        onChange(selected.filter((c) => c !== channel));
      } else {
        onChange([...selected, channel]);
      }
    },
    [selected, onChange],
  );

  const availableChannels = Object.entries(CHANNEL_CONFIG).filter(
    ([channel]) => channel !== CampaignChannel.WHATSAPP || isWhatsAppEnabled(),
  );

  return (
    <HStack spacing="sm" style={styles.container}>
      {availableChannels.map(([channel, config]) => {
        const isSelected = selected.includes(channel as CampaignChannel);
        const Icon = config.icon;

        return (
          <Box
            key={channel}
            pressable
            onPress={() => toggleChannel(channel as CampaignChannel)}
            borderRadius="md"
            padding="sm"
            backgroundColor={isSelected ? colors.primary.muted : 'transparent'}
            style={[styles.chip, isSelected && styles.chipSelected]}
          >
            <HStack spacing="xs" align="center">
              <Icon
                size={14}
                color={isSelected ? colors.primary[400] : colors.text.tertiary}
                strokeWidth={2}
              />
              <Text
                variant="caption"
                color={isSelected ? colors.primary[400] : colors.text.tertiary}
                style={styles.label}
              >
                {t(config.labelKey)}
              </Text>
            </HStack>
          </Box>
        );
      })}
    </HStack>
  );
}

const styles = StyleSheet.create({
  container: {
    flexWrap: 'wrap',
  },
  chip: {
    borderWidth: 1,
    borderColor: colors.border.default,
  },
  chipSelected: {
    borderColor: colors.primary.subtle,
  },
  label: {
    fontWeight: '600',
  },
});
